import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import Validator from 'validatorjs';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import {
  Form, Button, Modal, Header, Icon, Message,
} from 'semantic-ui-react';

import { dateTimeFormatted } from '../../utilities/dateUtils';

const NO_EXPIRATION_DATE = '9999-12-31 23:59:59 +0000';

const validationRules = {
  vin: 'required',
  sellerNumber: 'required',
};

const BadgingBlacklistForm = ({
  isModalOpen,
  onCloseHandler,
  onSubmitHandler,
  error,
  submitting,
  action,
  badgingBlacklist,
}) => {
  const [id, setId] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [vin, setVin] = useState('');
  const [sellerNumber, setSellerNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEmpty(badgingBlacklist)) {
      setId(badgingBlacklist.id);
      setExpirationDate(
        badgingBlacklist.expirationDate
          ? moment(badgingBlacklist.expirationDate)
          : null
      );
      setVin(badgingBlacklist.vin);
      setSellerNumber(badgingBlacklist.sellerNumber || '');
      setNotes(badgingBlacklist.notes || '');
    }
  }, [badgingBlacklist]);

  const date = expirationDate || null;

  const onChangeHandler = (setter, field) => event => {
    setter(event.target.value);
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const onDateChangeHandler = newDate => {
    setExpirationDate(newDate ? moment(newDate) : null);
  };

  const submitForm = () => {
    const formState = {
      id,
      expirationDate,
      vin,
      sellerNumber,
      notes,
    };
    const validation = new Validator(formState, validationRules);
    if (validation.fails()) {
      setErrors(validation.errors.all());
      return;
    }
    setErrors({});
    onSubmitHandler({
      id,
      expirationDate: expirationDate
        ? dateTimeFormatted(expirationDate)
        : NO_EXPIRATION_DATE,
      vin,
      sellerNumber,
      notes,
    });
  };

  return (
    <Modal
      data-testid='badging-blacklist-form-modal'
      open={isModalOpen}
      closeIcon
      onClose={onCloseHandler}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
    >
      <Modal.Header data-testid='badging-blacklist-form-title'>
        {!id && 'Create New Unit Badging Blacklist'}
        {id && 'Edit Unit Badging Blacklist'}
      </Modal.Header>
      <Modal.Content image>
        <Modal.Description>
          {error && (
            <Message negative>
              <p>{error}</p>
            </Message>
          )}
          <Form>
            <Header size='small' color='blue' />
            <Form.Group widths='equal'>
              <Form.Field required error={!isEmpty(errors.vin)}>
                <label htmlFor='vin'>VIN</label>
                <input
                  id='vin'
                  name='vin'
                  data-testid='badging-blacklist-vin-input'
                  value={vin}
                  onChange={onChangeHandler(setVin, 'vin')}
                  readOnly={action === 'edit'}
                  className={action === 'edit' ? 'input-disabled' : ''}
                />
                {errors.vin && <span>{errors.vin}</span>}
              </Form.Field>
              <Form.Field required error={!isEmpty(errors.sellerNumber)}>
                <label htmlFor='sellerNumber'>Seller ID</label>
                <input
                  id='sellerNumber'
                  name='sellerNumber'
                  data-testid='badging-blacklist-seller-input'
                  value={sellerNumber}
                  onChange={onChangeHandler(setSellerNumber, 'sellerNumber')}
                  readOnly={action === 'edit'}
                  className={action === 'edit' ? 'input-disabled' : ''}
                />
                {errors.sellerNumber && <span>{errors.sellerNumber}</span>}
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label htmlFor='expirationDate'>Expiration Date</label>
                <DatePicker
                  id='expirationDate'
                  customInput={(
                    <input
                      data-testid='badging-blacklist-expiration-input'
                      style={{ padding: '0', width: '100%' }}
                    />
                  )}
                  selected={date}
                  onChange={onDateChangeHandler}
                  showTimeSelect
                  timeFormat='HH:mm'
                  timeIntervals={15}
                  timeCaption='Time'
                  dateFormat='YYYY-MM-DD HH:mm:ss ZZ'
                />
              </Form.Field>
              <Form.Field>
                <label htmlFor='notes'>Notes</label>
                <input
                  id='notes'
                  name='notes'
                  data-testid='badging-blacklist-notes-input'
                  value={notes}
                  onChange={onChangeHandler(setNotes, 'notes')}
                />
              </Form.Field>
            </Form.Group>
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        {submitting && (
          <Button loading positive>
            Submitting...
          </Button>
        )}
        {!submitting && !id && (
          <Button
            data-testid='badging-blacklist-form-submit'
            color='green'
            onClick={submitForm}
            inverted
          >
            <Icon name='checkmark' /> Create
          </Button>
        )}
        {!submitting && id && (
          <Button
            data-testid='badging-blacklist-form-submit'
            color='green'
            onClick={submitForm}
            inverted
          >
            <Icon name='checkmark' /> Update
          </Button>
        )}
        <Button data-testid='badging-blacklist-form-cancel' onClick={onCloseHandler}>
          Cancel
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

BadgingBlacklistForm.propTypes = {
  isModalOpen: PropTypes.bool,
  onCloseHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  error: PropTypes.string,
  submitting: PropTypes.bool,
  action: PropTypes.string,
  badgingBlacklist: PropTypes.shape({
    id: PropTypes.string,
    expirationDate: PropTypes.string,
    vin: PropTypes.string,
    sellerNumber: PropTypes.string,
    notes: PropTypes.string,
  }),
};

BadgingBlacklistForm.defaultProps = {
  isModalOpen: false,
  error: null,
  submitting: false,
  action: 'new',
  badgingBlacklist: null,
};

export default BadgingBlacklistForm;
